import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usuarioService, Usuario } from '../../services/usuario';

const UsuarioTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar usuários
  const { data: usuarios = [], isLoading, error } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => usuarioService.getAllUsers(),
  });

  // Filtrar usuários baseado na busca
  const filteredUsuarios = useMemo(() => {
    if (!search.trim()) return usuarios;

    const searchLower = search.toLowerCase();
    return usuarios.filter((usuario) =>
      usuario.nomeFuncionario?.toLowerCase().includes(searchLower) ||
      usuario.usuario?.toLowerCase().includes(searchLower) ||
      usuario.role?.toLowerCase().includes(searchLower)
    );
  }, [usuarios, search]);

  // Paginação dos dados filtrados
  const paginatedUsuarios = useMemo(() => {
    return filteredUsuarios.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredUsuarios, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, usuario: Usuario) => {
    setAnchorEl(event.currentTarget);
    setSelectedUsuario(usuario);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUsuario(null);
  };

  const handleEdit = () => {
    if (selectedUsuario) {
      navigate(`/usuarios/editar/${selectedUsuario.id}`);
      handleMenuClose();
    }
  };

  const handleView = () => {
    if (selectedUsuario) {
      navigate(`/usuarios/visualizar/${selectedUsuario.id}`);
      handleMenuClose();
    }
  };

  const handleNew = () => {
    navigate('/usuarios/novo');
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar os dados dos usuários.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Filtrar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Novo Usuário
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 800 }}>
          {/* Cabeçalho da tabela */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr 80px',
            backgroundColor: 'grey.100',
            p: 2,
            fontWeight: 'bold',
            fontSize: '0.875rem',
            borderBottom: '2px solid',
            borderColor: 'grey.300'
          }}>
            <Box>Funcionário</Box>
            <Box>Nome de usuário</Box>
            <Box>Perfil</Box>
            <Box></Box>
          </Box>

          {/* Dados da tabela */}
          {paginatedUsuarios.map((usuario, index) => (
            <Box
              key={usuario.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 80px',
                p: 2,
                backgroundColor: index % 2 === 0 ? 'grey.50' : 'white',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <Box>{usuario.nomeFuncionario}</Box>
              <Box>{usuario.userName}</Box>
              <Box>{usuario.role}</Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, usuario)}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedUsuario?.id === usuario.id}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={handleEdit}>
                    <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                    Editar
                  </MenuItem>
                  <MenuItem onClick={handleView}>
                    <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
                    Visualizar
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {paginatedUsuarios.length === 0 && !isLoading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário cadastrado.'}
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Carregando...</Typography>
        </Box>
      )}

      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredUsuarios.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default UsuarioTable;
